import type { RESERVE_STEP } from '../views/Reserve';
import { WorkingReservationDraft } from '../../../../../../../shared/types/cartSession';

export interface FunnelContext {
  eventId?: string | null;
  eventTitle?: string | null;
  cartId?: string | null;
  activeDraft?: WorkingReservationDraft | null;
}

const STEP_URLS: Record<RESERVE_STEP, (ctx: FunnelContext) => string> = {
  grid: () => '/f/experiences',
  event: ctx => `/f/experience/${ctx.eventId ?? 'unknown'}/details`,
  date: ctx => `/f/experience/${ctx.eventId ?? 'unknown'}/date-time`,
  reservationFields: ctx => `/f/experience/${ctx.eventId ?? 'unknown'}/fill-reservation`,
  ticketFields: ctx => `/f/experience/${ctx.eventId ?? 'unknown'}/fill-tickets`,
  packages: ctx => `/f/experience/${ctx.eventId ?? 'unknown'}/packages`,
  addons: ctx => `/f/experience/${ctx.eventId ?? 'unknown'}/addons`,
  cart: ctx => `/f/experience/${ctx.eventId ?? 'unknown'}/cart`,
  payment: ctx => `/f/experience/${ctx.eventId ?? 'unknown'}/checkout`,
  complete: ({ cartId }) => `/f/cart/${cartId ?? 'unknown'}/complete`,
};

const STEP_EVENTS: Record<RESERVE_STEP, string> = {
  grid: 'Experiences Browsed',
  event: 'Experience Opened',
  date: 'Date Time Viewed',
  reservationFields: 'Reservation Entry Viewed',
  ticketFields: 'Ticket Entry Viewed',
  packages: 'Packages Viewed',
  addons: 'Addons Viewed',
  cart: 'Cart Viewed',
  payment: 'Checkout Viewed',
  complete: 'Reservation Completed',
};

const DRAFT_STEPS: RESERVE_STEP[] = [
  'ticketFields',
  'packages',
  'addons',
  'cart',
  'payment',
  'complete',
];

function getStepMetadata(step: RESERVE_STEP, ctx: FunnelContext): Record<string, unknown> {
  const meta: Record<string, unknown> = {};

  if (ctx.eventId) meta.eventId = ctx.eventId;
  if (ctx.eventTitle) meta.experienceTitle = ctx.eventTitle;
  if (ctx.cartId) meta.cartId = ctx.cartId;

  const draft = ctx.activeDraft?.reservationDraft;
  if (draft && DRAFT_STEPS.includes(step)) {
    const ticketCount = draft.tickets?.reduce((sum, t) => sum + (t.quantity ?? 0), 0) ?? 0;
    if (ticketCount > 0) meta.ticketCount = ticketCount;
  }
  if (draft && step === 'packages') {
    const packageCount = draft.packages?.reduce((sum, p) => sum + (p.quantity ?? 0), 0) ?? 0;
    if (packageCount > 0) meta.packageCount = packageCount;
  }
  if (draft && step === 'addons') {
    const addonCount = draft.addons?.reduce((sum, a) => sum + (a.quantity ?? 0), 0) ?? 0;
    if (addonCount > 0) meta.addonCount = addonCount;
  }

  return meta;
}

export function trackFunnelStep(step: RESERVE_STEP, ctx: FunnelContext, attempt = 0): void {
  if (!window.umami) {
    if (attempt < 10) setTimeout(() => trackFunnelStep(step, ctx, attempt + 1), 300);
    return;
  }

  const url = STEP_URLS[step](ctx);
  const eventName = STEP_EVENTS[step];
  const metadata = getStepMetadata(step, ctx);

  window.umami.track(props => ({ ...props, url }));
  window.umami.track(eventName, metadata);
}
